// PriceRangeSection.js
import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCurrency } from '../context/CurrencyContext';
import '../StylesComponents/price-range-section.css';

const PriceRangeSection = () => {
  const navigate = useNavigate();
  const { getCurrencySymbol, exchangeRates, currency } = useCurrency();
  const [autoActiveIndex, setAutoActiveIndex] = React.useState(0);
  const canvasRef = useRef(null);
  const mouseRef = useRef({ x: -1000, y: -1000 });
  
  const rate = exchangeRates[currency] || 1;
  const symbol = getCurrencySymbol();

  // Sequential scanning effect for mobile
  useEffect(() => {
    if (window.innerWidth > 768) return;
    const interval = setInterval(() => {
      setAutoActiveIndex((prev) => (prev + 1) % 4);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const priceRanges = [
    { title: `UNDER ${symbol}${Math.round(50 * rate)}`, value: 50, description: 'DAILY ESSENTIALS. REDUCED MARKUP.' },
    { title: `UNDER ${symbol}${Math.round(100 * rate)}`, value: 100, description: 'STOCKROOM STYLES. ACCESSIBLE LUXURY.' },
    { title: `UNDER ${symbol}${Math.round(200 * rate)}`, value: 200, description: 'PREMIUM SELECTIONS. OPTIMIZED VALUE.' },
    { title: `${symbol}${Math.round(500 * rate)}+`, value: 500, openEnded: true, description: 'INVESTMENT PIECES. STATEMENT STYLE.' },
  ];

  const handleRangeClick = (range) => {
    const convertedValue = Math.round(range.value * rate);
    if (range.openEnded) {
      navigate(`/all-brands?priceRanges=${convertedValue}%2B`);
    } else {
      navigate(`/all-brands?maxPrice=${convertedValue}`);
    }
  };

  // Canvas Background (Interactive Grid & Particles)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animationFrameId;

    const resize = () => {
      if (canvas && canvas.parentElement) {
        canvas.width = window.innerWidth;
        canvas.height = canvas.parentElement.offsetHeight;
      }
    };

    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouseRef.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      };
    };

    window.addEventListener('resize', resize);
    window.addEventListener('mousemove', handleMouseMove);
    resize();

    const particles = [];
    const particleCount = 60;

    for (let i = 0; i < particleCount; i++) {
      const type = Math.random();
      let pSymbol = null;
      if (type > 0.4) {
        if (Math.random() > 0.5) pSymbol = '$';
        else if (Math.random() > 0.5) pSymbol = '%';
        else pSymbol = 'OFF';
      }

      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: Math.random() * 4 + 2,
        speedX: Math.random() * 1 - 0.5,
        speedY: Math.random() * 1.5 + 0.5,
        opacity: Math.random() * 0.6 + 0.3,
        symbol: pSymbol
      });
    }

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Draw dynamic grid - Re-optimised for white background
      ctx.strokeStyle = 'rgba(0, 230, 118, 0.08)'; 
      ctx.lineWidth = 1.5;
      const gridSize = 80;
      const scrollOffset = (Date.now() / 40) % gridSize;
      
      for(let x = 0; x < canvas.width; x += gridSize) {
        ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, canvas.height); ctx.stroke();
      }
      for(let y = scrollOffset; y < canvas.height; y += gridSize) {
        ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(canvas.width, y); ctx.stroke();
      }

      // Draw Connection Lines (Neural Mesh)
      ctx.lineWidth = 1;
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          
          if (dist < 180) {
            ctx.strokeStyle = `rgba(0, 230, 118, ${0.3 * (1 - dist / 180)})`;
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }

      // Draw & Update Particles
      particles.forEach(p => {
        const mdx = p.x - mouseRef.current.x;
        const mdy = p.y - mouseRef.current.y;
        const mdist = Math.sqrt(mdx * mdx + mdy * mdy);
        if (mdist < 250) {
          const force = (250 - mdist) / 250;
          p.x += (mdx / mdist) * force * 8;
          p.y += (mdy / mdist) * force * 8;
        }

        if (p.symbol) {
          ctx.fillStyle = `rgba(0, 230, 118, ${p.opacity + 0.2})`;
          ctx.font = p.symbol === 'OFF' ? 'bold 10px monospace' : 'bold 18px monospace';
          ctx.fillText(p.symbol, p.x, p.y);
          ctx.shadowBlur = 8;
          ctx.shadowColor = 'rgba(0, 230, 118, 0.4)';
        } else {
          ctx.fillStyle = `rgba(0, 230, 118, ${p.opacity + 0.2})`;
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
          ctx.fill();
          ctx.shadowBlur = 10;
          ctx.shadowColor = 'rgba(0, 230, 118, 0.5)';
        }

        p.y += p.speedY;
        p.x += p.speedX;

        if (p.y > canvas.height) p.y = -20;
        if (p.x > canvas.width) p.x = 0;
        if (p.x < 0) p.x = canvas.width;
        
        ctx.shadowBlur = 0;
      });

      animationFrameId = requestAnimationFrame(draw);
    };

    draw();
    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', handleMouseMove);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <section className="price-range-section">
      <div className="vignette-overlay"></div>
      <canvas ref={canvasRef} className="price-range-canvas"></canvas>
      
      <div className="price-range-wrapper">
        <div className="price-range-header">
          <h2 className="price-range-heading">SHOP BY BUDGET</h2>
        </div>

        <div className="price-range-container">
          {priceRanges.map((range, index) => (
            <div
              key={index}
              onClick={() => handleRangeClick(range)}
              className={`price-box ${autoActiveIndex === index ? 'mobile-auto-active' : ''}`}
              style={{ cursor: 'pointer' }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleRangeClick(range);
                }
              }}
              aria-label={`Shop ${range.title}`}
            >
              <div className="price-box-content">
                <h2 className="price-box-title">{range.title}</h2>
                <p className="price-box-description">{range.description}</p>
                <div className="price-box-cta">
                  <span className="cta-text">Shop Collection</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PriceRangeSection;
